import { useEffect, useRef, useCallback } from 'react';

interface UseInactivityTimerProps {
  timeout: number;
  onTimeout: () => void;
  onWarning?: (remainingTime: number) => void;
  warningTime?: number;
  enabled?: boolean;
}

export const useInactivityTimer = ({
  timeout,
  onTimeout,
  onWarning,
  warningTime = 2 * 60 * 1000,
  enabled = true
}: UseInactivityTimerProps) => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const warningTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const lastActivityRef = useRef<number>(Date.now());

  const resetTimer = useCallback(() => {
    if (!enabled) return;

    lastActivityRef.current = Date.now();

    // Limpa timers existentes
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
    }

    // Define o timer de aviso
    if (onWarning && warningTime > 0) {
      warningTimeoutRef.current = setTimeout(() => {
        // Ao iniciar o aviso, o tempo restante é exatamente o warningTime
        const remainingTime = warningTime;
        onWarning(remainingTime);
      }, timeout - warningTime);
    }

    // Define o timer principal
    timeoutRef.current = setTimeout(() => {
      onTimeout();
    }, timeout);
  }, [timeout, onTimeout, onWarning, warningTime, enabled]);

  useEffect(() => {
    if (!enabled) {
      // Limpa todos os timers se desabilitado
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
      return;
    }

    // Lista de eventos que indicam atividade do usuário
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'focus'
    ];

    // Função que será chamada nos eventos de atividade
    const handleActivity = () => {
      resetTimer();
    };

    // Adiciona listeners para todos os eventos de atividade
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // Inicia o timer
    resetTimer();

    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
    };
  }, [resetTimer, enabled]);

  // Retorna função para resetar manualmente o timer
  return {
    resetTimer,
    getLastActivity: () => lastActivityRef.current
  };
};
